import { cancelBooking as cancelBookingSdk } from '@/api/generated/sdk.gen';
import type { CancelBookingResponse } from '@/api/generated/types.gen';
import { formatMoney } from '@/lib/format';

export type { CancelBookingResponse };

/**
 * Cancel a booking WE own — an ARI-only direct/manual booking (ADR-034). The
 * generated SDK client carries the CSRF interceptor (see main.tsx), so the POST
 * is safe to route through it. Throws on non-2xx; callers branch on the thrown
 * Problem's `.status` / `.code` (409 `ota_channex_booking` → cancel in Channex;
 * 409 `not_cancellable`; 403; 503). OTA/Channex bookings are NOT cancellable here.
 */
export async function cancelBooking(id: string): Promise<CancelBookingResponse> {
  const res = await cancelBookingSdk({ throwOnError: true, path: { id } });
  return res.data;
}

/**
 * Build the success-toast message for a completed cancel. Shared by the
 * drawer and the detail page (mirrors cancelErrorToast below) so the two
 * surfaces never drift.
 *
 * Five states now (Task 12 review round 1 item 8 added the fifth) —
 * matching what the API can actually report (whole-branch review, OH-68 +
 * OH-166 Task 12): amount_minor/currency are an `Opt` field that can be
 * ABSENT even when refunded/already_refunded is true (a partially_refunded,
 * or partial-but-still-settling refund_pending, payment never persists its
 * exact refunded amount anywhere re-readable — see
 * bookings_refund_shared.go). Collapsing "refunded, amount unknown" into
 * the same message as "not refunded at all" would silently drop the fact
 * that money moved. refund_status's `in_flight` value (Task 12 — the cancel
 * path now opens a refund obligation and sends it through the finance
 * orchestrator; a provider timeout/refusal no longer 503s the whole cancel,
 * it durably records the decision and moves on) is a state this function
 * covers: the cancel succeeded but the refund's own outcome is not
 * confirmed yet — collapsing THAT into "not refunded at all" would tell the
 * operator nothing is happening when a request is actually in flight.
 * `needs_attention` is DISTINCT from `in_flight`: the provider gave a
 * DEFINITE refusal — no money moved, and nothing will resolve this on its
 * own; an operator must act on the finance screen. Telling them "awaiting
 * confirmation" (the in_flight message) would be actively wrong here.
 */
export function cancelSuccessMessage(res: CancelBookingResponse): string {
  if (res.refund_status === 'needs_attention') {
    return 'Cancelled — the refund was declined by the provider and needs attention on the finance screen.';
  }
  if (res.refund_status === 'in_flight') {
    return 'Cancelled — refund submitted, awaiting confirmation from the provider.';
  }
  const refunded = res.refunded || res.already_refunded;
  if (!refunded) return 'Nights released to the calendar.';
  if (res.amount_minor != null && res.currency) {
    return `${formatMoney(res.amount_minor, res.currency)} refunded to the guest.`;
  }
  return 'Refund processed — see the payment for the exact amount.';
}

/**
 * Map a thrown cancel error (RFC 7807 Problem JSON) to toast args. Shared by the
 * drawer and the detail page so the messaging stays consistent.
 */
export function cancelErrorToast(err: unknown): {
  kind: 'info' | 'error';
  title: string;
  message: string;
} {
  const p = err as { status?: number; code?: string; detail?: string; title?: string };
  if (p.status === 409 && p.code === 'ota_channex_booking') {
    return { kind: 'info', title: 'Cancel with the channel', message: "OTA bookings are cancelled in the channel manager's dashboard." };
  }
  if (p.status === 409) {
    return { kind: 'error', title: 'Cannot cancel', message: p.detail ?? 'Booking is no longer cancellable.' };
  }
  if (p.status === 403) {
    return { kind: 'error', title: 'Not allowed', message: 'Insufficient permissions (finance / super_admin).' };
  }
  return { kind: 'error', title: 'Cancel failed', message: p.detail ?? p.title ?? 'Could not cancel the booking.' };
}
